export const CUSTOMER_RETURN_TARGET_STORAGE_KEY = "meta-video:customer-return-target";

export interface ReturnTargetStorage {
  getItem(key: string): string | null;
  removeItem(key: string): void;
  setItem(key: string, value: string): void;
}

const CUSTOMER_PATHS = new Set(["/", "/saved-views"]);

export function normalizeCustomerReturnTarget(
  input: string,
  origin: string,
): string | null {
  if (
    input.length === 0 ||
    input !== input.trim() ||
    /[\\\u0000-\u001f\u007f]/u.test(input) ||
    input.startsWith("//")
  ) {
    return null;
  }

  let expectedOrigin: URL;
  let target: URL;
  try {
    expectedOrigin = new URL(origin);
    target = new URL(input, expectedOrigin);
  } catch {
    return null;
  }

  const isRootRelative = input.startsWith("/");
  const isAbsoluteSameOrigin = !isRootRelative && /^[a-z][a-z\d+.-]*:/iu.test(input);
  if (
    (!isRootRelative && !isAbsoluteSameOrigin) ||
    target.origin !== expectedOrigin.origin ||
    target.username !== "" ||
    target.password !== "" ||
    !CUSTOMER_PATHS.has(target.pathname)
  ) {
    return null;
  }

  return `${target.pathname}${target.search}${target.hash}`;
}

export function storeCustomerReturnTarget(
  storage: ReturnTargetStorage | null,
  target: string,
  origin: string,
): boolean {
  if (storage === null) return false;
  const normalized = normalizeCustomerReturnTarget(target, origin);
  try {
    if (normalized === null) {
      storage.removeItem(CUSTOMER_RETURN_TARGET_STORAGE_KEY);
      return false;
    }
    storage.setItem(CUSTOMER_RETURN_TARGET_STORAGE_KEY, normalized);
    return true;
  } catch {
    return false;
  }
}

export function storeCustomerReturnTargetIfMissing(
  storage: ReturnTargetStorage | null,
  target: string,
  origin: string,
): boolean {
  if (storage === null) return false;
  try {
    const stored = storage.getItem(CUSTOMER_RETURN_TARGET_STORAGE_KEY);
    if (stored !== null && normalizeCustomerReturnTarget(stored, origin) !== null) {
      return true;
    }
  } catch {
    return false;
  }
  return storeCustomerReturnTarget(storage, target, origin);
}

export function consumeCustomerReturnTarget(
  storage: ReturnTargetStorage | null,
  origin: string,
): string | null {
  if (storage === null) return null;
  try {
    const stored = storage.getItem(CUSTOMER_RETURN_TARGET_STORAGE_KEY);
    storage.removeItem(CUSTOMER_RETURN_TARGET_STORAGE_KEY);
    return stored === null ? null : normalizeCustomerReturnTarget(stored, origin);
  } catch {
    return null;
  }
}
